import {
  createRootRoute,
  Link,
  Outlet,
  ScrollRestoration,
  useCanGoBack,
  useRouter,
  useMatchRoute,
  useRouterState,
  useSearch,
} from '@tanstack/react-router';
import { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { Logo } from '~/generated/svg/logo/Logo';
import { Icon as LogoMark } from '~/generated/svg/logo/Icon';
import { Menu as MenuIcon } from '~/generated/svg/menu/Menu';
import { DefaultErrorComponent } from '~/components/DefaultErrorComponent';
import { NavigationToolTip } from '~/components/NavigationToolTip';
import { clsx } from 'clsx';
import { Icon } from '~/components/Icon';
import { useTranslate } from '@tolgee/react';
import { MenuItemLink } from '~/components/MenuItemLink';
import type { RootLoaderData } from '@agentselly/immodossier-shared';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery, isLoaderQueryPending } from '~/helper/useLoaderQuery';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { AuthLoadErrorWidget } from '~/components/widget/base/AuthLoadErrorWidget';
import { useClerkLoadTimeout } from '~/helper/useClerkLoadTimeout';
import { NotFoundWidget } from '~/components/widget/base/NotFoundWidget';
import { SignOutButton, useUser } from '@clerk/clerk-react';
import type { MenuItem } from '~/model/MenuItem';
import type { UseQueryResult } from '@tanstack/react-query';
import { useAppParams } from '~/helper/useAppParams';
import { getSignOutRedirectUrl } from '~/helper/getSignOutRedirectUrl';
import { WhitelabelStyle } from '~/components/WhitelabelStyle';
import { useLinkClickTracking } from '~/helper/trackLinkClicks';
import { AuthzProvider } from '~/context/AuthzContext';

const TanStackRouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-router-devtools').then((m) => ({
        default: m.TanStackRouterDevtools,
      }))
    )
  : () => null;

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({
        default: m.ReactQueryDevtools,
      }))
    )
  : () => null;
import { AssetConfigProvider } from '~/context/AssetConfigContext';
import { GlobalLoaderProvider } from '~/context/GlobalLoaderContext';
import { ThemeProvider } from '~/context/ThemeContext';
import { usePageTitle } from '~/helper/usePageTitle';
import { Toaster } from 'sonner';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { BackButton } from '~/components/BackButton';
import { useMainScrollRestoration } from '~/hooks/useMainScrollRestoration';
import {
  isPropertyEditRoute,
  getPropertyEditFallbackHash,
} from '~/model/NavigationRegistry';
import { BackButtonSidebarItem } from '~/components/navigation/BackButtonSidebarItem';
import { shouldRedirectToLogin, isPublicPath } from '~/helper/authGuard';
import { redirect } from '@tanstack/react-router';

export const Route = createRootRoute({
  beforeLoad: ({ context, location }) => {
    const { clerkAuth } = context as {
      clerkAuth?: { isLoaded: boolean; isSignedIn: boolean };
    };
    if (
      clerkAuth &&
      shouldRedirectToLogin(
        location.pathname,
        clerkAuth.isLoaded,
        clerkAuth.isSignedIn,
      )
    ) {
      // Build redirect URL with redirect-back info
      const searchParams = new URLSearchParams();
      // Preserve the original path so the login page can redirect back after auth
      if (location.pathname && location.pathname !== '/auth/login') {
        searchParams.set('redirect_url', location.pathname);
      }
      // Redirect to the REAL login route. The legacy top-level '/auth/login'
      // route does not exist in the route tree — throwing toward it makes
      // TanStack Router silently fail the navigation, leaving the user stuck
      // on the protected page. The login page is
      // '/$lang/$companyId/public/auth/login'.
      const [
        lang,
        companyId,
      ] = location.pathname.split('/').filter(Boolean);
      throw redirect({
        to: AppRoute.$LANG_$COMPANYID_PUBLIC_AUTH_LOGIN as any,
        params: {
          lang: lang || 'de',
          companyId: companyId || '70927429839',
        } as any,
        search: Object.fromEntries(searchParams) as any,
        replace: true,
      });
    }
  },
  component: Root,
  notFoundComponent: NotFoundWidget,
  errorComponent: (errorProps) => {
    // Let TanStack Router handle Not Found
    if (errorProps.error?.message === 'Not Found') {
      return null;
    }

    console.error(errorProps.error);

    return (
      <RootLayout>
        <DefaultErrorComponent {...errorProps} />
      </RootLayout>
    );
  },
});

function Root() {
  useLinkClickTracking();

  return (
    <RootLayout>
      <ScrollRestoration />
      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{ duration: 4000 }}
      />
      <Outlet />
      {import.meta.env.DEV ? (
        <Suspense fallback={null}>
          <div className="hidden md:block">
            <TanStackRouterDevtools />
            <ReactQueryDevtools />
          </div>
        </Suspense>
      ) : null}
    </RootLayout>
  );
}

function RootLayout(props: { readonly children: React.ReactNode }) {
  const params = useAppParams(Route);

  if ('valiantDeviceId' in params) {
    return (
      <>
        <WhitelabelStyle companyId={params.companyId} />
        {props.children}
      </>
    );
  }

  return (
    <>
      <WhitelabelStyle companyId={params.companyId} />
      <Layout>{props.children}</Layout>
    </>
  );
}

function Layout(props: LayoutProps) {
  const { t } = useTranslate();

  const routerState = useRouterState();
  const params = useAppParams(Route);
  const pageTitleData = usePageTitle();
  const search: {
    readonly valiantDeviceId?: string;
  } = useSearch(Route as any);

  useMainScrollRestoration();
  const matchRoute = useMatchRoute();

  const [
    isUserMenuOpen,
    setIsUserMenuOpen,
  ] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const [
    isMobileMenuOpen,
    setIsMobileMenuOpen,
  ] = useState(false);

  const { isSignedIn, isLoaded, user } = useUser();

  const query: UseQueryResult<RootLoaderData | null, Error> = useLoaderQuery(
    AppRoute.ROOT,
    {
      currentRoute: routerState.location?.pathname,
      params: {
        // Since we have no params, we can leave this empty
      },
    },
  );

  // If Clerk never becomes ready (blocked script/adblocker) OR the root
  // loader query hangs (e.g. a stale/invalid Clerk session cookie makes the
  // backend loader stall), fail visibly after a short grace period instead of
  // showing an infinite spinner. `ready` is only true once BOTH Clerk is
  // loaded AND the root loader query has produced data/error — otherwise the
  // timeout would never fire for a signed-in user whose loader request hangs.
  const clerkLoadTimedOut = useClerkLoadTimeout(
    !!isLoaded && !isLoaderQueryPending(query),
  );

  // A protected (non-public) route must never render a disabled loader query
  // forever when the user is not signed in. The root loader query is disabled
  // (`enabled = isLoaded && (isPublic || !!isSignedIn)`) for a signed-out user
  // on a protected route, so it stays `isPending`/idle and the render branch
  // below shows the LoadingWidget — and the child route's own redirect never
  // runs because children are not rendered while the query is pending.
  //
  // The root `beforeLoad` guard is NOT a reliable redirect here: it throws
  // toward the non-existent top-level `/auth/login` route, which TanStack
  // Router cannot resolve, so the navigation silently fails and the URL stays
  // on the protected page. Redirect reliably via the real, existing login
  // route instead.
  const router = useRouter();
  const isPublicRoute = isPublicPath(location.pathname);
  const isProtectedSignedOut = isLoaded && !isSignedIn && !isPublicRoute;

  useEffect(() => {
    if (!isProtectedSignedOut) {
      return;
    }
    void router.navigate({
      to: AppRoute.$LANG_$COMPANYID_PUBLIC_AUTH_LOGIN as any,
      params: {
        lang: params.lang,
        companyId: params.companyId,
      } as any,
      replace: true,
    });
  }, [
    isProtectedSignedOut,
    router,
    params.lang,
    params.companyId,
  ]);

  const signOutRedirectUrl = getSignOutRedirectUrl(params);

  const rootMenuItem: MenuItem = isSignedIn
    ? {
        route: AppRoute.$LANG_$COMPANYID_$CONTACTID_INDEX,
        params: params,
        name: 'nav_home',
        icon: 'home',
      }
    : {
        href: '/',
        name: 'nav_home',
        icon: 'home',
      };

  const userNavigation: MenuItem[] = query.data?.showUserNavigation
    ? [
        {
          route: AppRoute.$LANG_$COMPANYID_$CONTACTID_PROFILE,
          params: params,
          name: 'user_profile',
        },
      ]
    : [];
  const sidebarNavigation: MenuItem[] = [
    rootMenuItem,
    {
      route: AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_HOME,
      params: params,
      name: 'nav_dossier',
      icon: 'dossier',
      mobile: true,
      hidden:
        isPublicRoute || !query.data?.showDossierArea || !params.dossierId,
      children: [
        {
          route: AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_HOME,
          params: params,
          name: 'nav_dashboard',
          icon: 'dashboard',
          mobile: true,
        },
        {
          route:
            AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_EVALUATION,
          params: params,
          name: 'nav_evaluation',
          icon: 'evaluation',
          mobile: true,
        },
        {
          route: AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_REGION,
          params: params,
          name: 'nav_region',
          icon: 'region',
          mobile: true,
        },
        {
          route: AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_MARKET,
          params: params,
          name: 'nav_market',
          icon: 'market',
          mobile: true,
        },
      ],
    } as MenuItem,
    {
      route: AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME,
      params: params,
      name: 'nav_cockpit',
      icon: 'pieLineGraphDesktop',
      mobile: true,
      hidden:
        isPublicRoute || !query.data?.showCockpitArea || !params.propertyId,
      children: [
        {
          route: AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME,
          params: params,
          name: 'nav_dashboard',
          icon: 'pieLineGraphDesktop',
          mobile: true,
        },
        {
          route:
            AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_ACTIVITIES,
          params: params,
          name: 'nav_cockpit_activities',
          icon: 'listBullets',
          mobile: true,
        },
        {
          route:
            AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_CALENDAR,
          params: params,
          name: 'nav_cockpit_calendar',
          icon: 'calendar3',
          mobile: true,
        },
        {
          route:
            AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_INTEREST,
          params: params,
          name: 'nav_cockpit_interest',
          icon: 'mulitpleActionsView',
          mobile: true,
        },
        {
          route:
            AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_OFFERS,
          params: params,
          name: 'nav_cockpit_offers',
          icon: 'cashPaymentBag1',
          mobile: true,
        },
        {
          route:
            AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_STATISTICS,
          params: params,
          name: 'nav_cockpit_statistics',
          icon: 'market',
          mobile: true,
        },
        {
          route:
            AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_DOCUMENTS,
          params: params,
          name: 'nav_cockpit_documents',
          icon: 'fileText',
          mobile: true,
        },
      ],
    } as MenuItem,
    {
      route: isSignedIn
        ? AppRoute.$LANG_$COMPANYID_$CONTACTID_CONTACT
        : AppRoute.$LANG_$COMPANYID_PUBLIC_CONTACT,
      params: {
        ...params,
        contactId: user?.publicMetadata?.hubspotContactId || '',
      } as any,
      name: 'nav_contact',
      icon: 'contact',
      mobile: true,
    } as MenuItem,
    {
      route: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_INDEX,
      params: params,
      name: 'nav_demo',
      icon: 'demo',
      mobile: true,
      // Demo previews are public — both Dossier and Cockpit are always
      // available regardless of account permissions/rights.
      hidden: false,
      children: [
        {
          route: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_DOSSIER_HOME,
          params: params,
          name: 'nav_demo_dossier',
          icon: 'dossier',
          mobile: true,
          hidden: false,
        },
        {
          route: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_COCKPIT_HOME,
          params: params,
          name: 'nav_demo_cockpit',
          icon: 'pieLineGraphDesktop',
          mobile: true,
          hidden: false,
        },
      ],
    } as MenuItem,
    // ADMIN
    {
      route: AppRoute.$LANG_$COMPANYID_ADMIN_HOME,
      params: params,
      name: 'nav_admin',
      icon: 'admin',
      mobile: true,
      hidden: isPublicRoute || !query.data?.showAdminArea,
      children: [
        {
          route: AppRoute.$LANG_$COMPANYID_ADMIN_HOME,
          params: params,
          name: 'nav_admin_home',
          icon: 'admin',
          mobile: true,
        },
        {
          route: AppRoute.$LANG_$COMPANYID_ADMIN_EVALUATIONS,
          params: params,
          name: 'nav_admin_evaluations',
          icon: 'evaluations',
          mobile: true,
        },
        {
          route: AppRoute.$LANG_$COMPANYID_ADMIN_USERS,
          params: params,
          name: 'nav_admin_users',
          icon: 'users',
          mobile: true,
        },
      ],
    } as MenuItem,
  ].filter((it) => !it.hidden);

  /**
   * NavigationRegistry-basierte Active-Prüfung.
   * `getMainMenuKey` und `getSubmenuKey` liefern die zentrale Matrix-Zuordnung
   * für jede Route-ID. Kein includes, kein lokaler Click-State, kein pending:true,
   * keine duplizierte Matchlogik.
   *
   * Wichtig: `fuzzy: true` wird NICHT verwendet. `fuzzy: true` würde bei
   * Child-Routen (z.B. dossier/$dossierId/evaluation) auch den Parent-Eintrag
   * (dossier/$dossierId/) matchen — das führt zu mehreren aktiven Items.
   * Stattdessen matched `isRouteActive` exakt. Parent-Menüeinträge (z.B.
   * "Dossier") werden automatisch aktiv, wenn eines ihrer Children aktiv ist
   * (siehe `updateMenuItemActiveState` → `children.some()`).
   *
   * Wichtig: `pending: true` wird NICHT verwendet. Der committed (abgeschlossene)
   * Router-State ist die Single Source of Truth. Während einer pending Navigation
   * bleibt der bisher aktive Tab stabil; nach Commit wird der neue aktiv.
   * So wird der Submenü-Flicker vermieden.
   */
  function isRouteActive(route: AppRoute | string): boolean {
    // Exakter Match (inkl. dynamischer Params). Kein fuzzy:true — das würde
    // bei Parent-Routen (z.B. dossier/$dossierId/) fälschlicherweise auch
    // Child-Routen matchen und zu mehreren aktiven Items führen.
    return !!matchRoute({ to: route } as any);
  }

  function updateMenuItemActiveState(menuItem: MenuItem) {
    const isActive =
      (menuItem.route && isRouteActive(menuItem.route)) ||
      menuItem.children?.some((it) => it.active);

    (menuItem as any).active = isActive;
  }

  // Set active state of each navigation item
  for (const menuItem of sidebarNavigation) {
    for (const childMenuItem of menuItem.children || []) {
      if (childMenuItem.children?.length) {
        throw new Error('Nested children are not supported');
      }

      updateMenuItemActiveState(childMenuItem);
    }

    updateMenuItemActiveState(menuItem);
  }

  const activePrimaryNavigation = sidebarNavigation.find((it) => it.active);
  const primaryNavigation: MenuItem[] = sidebarNavigation.map((it) => ({
    ...it,
    children: undefined,
  }));

  // Demo sub-choices (Dossier / Cockpit) — rendered inline inside the mobile
  // burger drawer so the user can pick a preview directly, without first
  // having to land on a demo page to reveal the secondary navigation.
  const demoNavItem = sidebarNavigation.find((it) => it.name === 'nav_demo');
  const demoChildren: MenuItem[] = (demoNavItem?.children || []).filter(
    (it) => !it.hidden,
  );

  // Check if we're on a property edit page using committed route IDs.
  // Using matchRoute({pending:true}) would flicker during transitions between
  // property edit tabs because matchRoute briefly returns false for all routes
  // between the old match being removed and the pending match being set.
  // Committed matches are stable: during a tab transition, the old route is still
  // committed until the new one resolves, so isPropertyEditPage stays true.
  const committedRouteId =
    routerState.matches[routerState.matches.length - 1]?.routeId || '';
  const isPropertyEditPage = isPropertyEditRoute(committedRouteId);

  // Fallback hash for property edit back button — determined by NavigationRegistry.
  const getPropertyEditFallbackHashValue = () =>
    getPropertyEditFallbackHash(committedRouteId);

  // Property edit submenu items (added to secondary navigation)
  const propertyEditSubmenu: MenuItem[] = isPropertyEditPage
    ? [
        {
          route: AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_HOME,
          params: params,
          name: 'back_to_overview',
          icon: 'arrowLeft1',
          mobile: true,
        },
        {
          route:
            AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_PROPERTY_EDIT,
          params: params,
          name: 'nav_property_edit',
          icon: 'immobilieninfo',
          mobile: true,
        },
        {
          route:
            AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_PROPERTY_CONDITION_EDIT,
          params: params,
          name: 'nav_property_condition',
          icon: 'dashboard',
          mobile: true,
        },
        {
          route:
            AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_PROPERTY_QUALITIES_EDIT,
          params: params,
          name: 'nav_property_quality',
          icon: 'evaluation',
          mobile: true,
        },
      ]
    : [];

  // Set active state for property edit submenu items
  for (const menuItem of propertyEditSubmenu) {
    updateMenuItemActiveState(menuItem);
  }

  // Secondary navigation - show both dossier submenu AND property edit submenu on property edit pages
  const baseSecondaryNavigation = activePrimaryNavigation?.children || [];

  const secondaryNavigation: MenuItem[] = isPropertyEditPage
    ? [
        ...baseSecondaryNavigation,
        ...propertyEditSubmenu,
      ]
    : baseSecondaryNavigation;

  // Keep previous secondary nav to avoid flicker during route transitions
  const prevSecondaryRef = useRef<MenuItem[]>([]);
  const stableSecondaryNavigation: MenuItem[] =
    secondaryNavigation.length === 0 &&
    (query.isFetching || query.isLoading) &&
    prevSecondaryRef.current.length
      ? prevSecondaryRef.current
      : secondaryNavigation;

  useEffect(() => {
    if (secondaryNavigation.length > 0) {
      prevSecondaryRef.current = secondaryNavigation;
    }
  }, [secondaryNavigation]);

  // Set the current user in the global window object
  useEffect(() => {
    const routeId =
      routerState.matches[routerState.matches.length - 1]?.routeId ||
      routerState.location?.pathname;
    if (!routeId) {
      return;
    }

    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);

    // Pageviews are tracked automatically by Pirsch's `pa.js` (see
    // main.tsx, injectPirschTracking) on every History API navigation.
    // No manual pageview call here — that would double-count every route
    // change as two pageviews.
  }, [
    routerState.location,
  ]);

  // Close user dropdown on outside click
  useEffect(() => {
    if (!isUserMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isUserMenuOpen]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    const original = document.body.style.overflow;
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = original;
    }
    return () => {
      document.body.style.overflow = original;
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsUserMenuOpen(false);
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    isUserMenuOpen,
    isMobileMenuOpen,
  ]);

  const openMobileMenu = () => {
    setIsMobileMenuOpen(true);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Demo nav items (Dossier/Cockpit previews) used to shout a full-caps
  // "DEMO Dossier" / "DEMO Cockpit" label that competed with the page's
  // primary content. Per UI/UX hierarchy guidelines, promo/secondary nav must
  // read as subordinate — render a small muted "Demo" badge + the plain label
  // instead of the loud all-caps prefix. Theme adapts to the dark sidebar vs
  // the light bottom bar / drawer. Badge uses a green accent for visibility.
  const renderNavLabel = (
    item: MenuItem,
    opts: { theme: 'light' | 'dark'; stack?: boolean } = { theme: 'light' },
  ) => {
    const isDemo =
      item.name === 'nav_demo' ||
      item.name === 'nav_demo_dossier' ||
      item.name === 'nav_demo_cockpit';
    if (!isDemo) return t(item.name);
    const base =
      item.name === 'nav_demo_dossier'
        ? t('nav_dossier', 'Dossier')
        : item.name === 'nav_demo_cockpit'
        ? t('nav_cockpit', 'Cockpit')
        : null;
    return (
      <span
        className={clsx(
          'inline-flex items-center justify-center gap-1',
          opts.stack && 'flex-col gap-0.5',
        )}
      >
        <span
          className={clsx(
            'rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider shadow-sm dark:shadow-gray-900/50 ring-1 ring-inset',
            opts.theme === 'dark'
              ? 'bg-green-500/20 text-green-300 ring-green-500/30'
              : 'bg-green-50 text-green-700 ring-green-700/20',
          )}
        >
          {t('nav_demo_badge', 'Demo')}
        </span>
        {base && <span>{base}</span>}
      </span>
    );
  };

  return (
    <ThemeProvider>
      <AssetConfigProvider
        publicAssetServerUrl={query.data?.publicAssetServerUrl}
      >
        <AuthzProvider
          role={(query.data as any)?.role}
          permissions={(query.data as any)?.permissions}
        >
          <GlobalLoaderProvider>
            <div className="flex h-dvh overflow-hidden">
              {/* Sidebar */}
              <div className="app-sidebar hidden md:flex flex-col bg-gray-800 dark:bg-[#18181b] w-28 overflow-y-auto">
                <div className="flex flex-col items-center space-y-1 px-2">
                  <MenuItemLink
                    item={{
                      route: AppRoute.$LANG_$COMPANYID_INDEX,
                      params: params,
                      name: 'nav_home',
                    }}
                    className="group flex flex-col items-center py-5 space-y-4 text-white text-xs"
                  >
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20 transition-colors group-hover:bg-white/15 group-hover:ring-white/40">
                      <Logo className="h-7 w-auto" />
                    </div>
                    <span className="flex flex-col items-center leading-tight">
                      <span className="font-semibold tracking-wide text-gray-300 group-hover:text-white transition-colors">
                        ImmoDossier
                      </span>
                    </span>
                  </MenuItemLink>
                  {primaryNavigation.map((item: MenuItem, idx: number) => {
                    const isDemo = !!item.name && item.name.includes('demo');
                    if (item.showLoginMessage) {
                      return (
                        <NavigationToolTip
                          key={idx}
                          navigationItem={
                            <MenuItemLink
                              item={{} as MenuItem}
                              className={clsx(
                                item.hidden && !item.active ? 'hidden' : '',
                                item.active
                                  ? 'bg-primary-800 text-white'
                                  : 'text-gray-50 hover:bg-primary-800 hover:text-white',
                                'relative group w-full p-3 rounded-md flex flex-col items-center text-xs font-medium',
                              )}
                              aria-current={item.active ? 'page' : undefined}
                            >
                              <Icon
                                icon={item.icon}
                                className={clsx(
                                  item.active
                                    ? 'text-white'
                                    : 'text-gray-100 group-hover:text-white',
                                  'h-6 w-6',
                                )}
                                aria-hidden="true"
                              />
                              <span className="mt-2">
                                {renderNavLabel(item, { theme: 'dark' })}
                              </span>
                              {item.showNotification && (
                                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-orange-500 ring-2 ring-gray-800" />
                              )}
                            </MenuItemLink>
                          }
                        />
                      );
                    }

                    return (
                      <MenuItemLink
                        key={idx}
                        item={item}
                        className={clsx(
                          item.hidden && !item.active ? 'hidden' : '',
                          item.active
                            ? 'bg-primary-800 text-white'
                            : isDemo
                            ? 'text-gray-400 hover:bg-primary-800 hover:text-gray-100'
                            : 'text-gray-50 hover:bg-primary-800 hover:text-white',
                          'relative group w-full p-3 rounded-md flex flex-col items-center text-xs',
                          isDemo ? 'font-normal' : 'font-medium',
                        )}
                        aria-current={item.active ? 'page' : undefined}
                      >
                        <Icon
                          icon={item.icon}
                          className={clsx(
                            item.active
                              ? 'text-white'
                              : isDemo
                              ? 'text-gray-400 group-hover:text-gray-200'
                              : 'text-gray-100 group-hover:text-white',
                            'h-6 w-6',
                          )}
                          aria-hidden="true"
                        />
                        <span className="mt-2 text-center">
                          {renderNavLabel(item, { theme: 'dark' })}
                        </span>
                        {item.showNotification && (
                          <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-orange-500 ring-2 ring-gray-800" />
                        )}
                      </MenuItemLink>
                    );
                  })}
                </div>
              </div>
              {/* Secondary navigation (always mounted to avoid layout shifts) */}
              <div
                className={clsx(
                  'app-subsidebar bg-white dark:bg-[#18181b] hidden md:flex overflow-x-hidden transition-all duration-200 ease-out border-r border-gray-200 dark:border-[#27272a]',
                  stableSecondaryNavigation.length
                    ? 'w-28 opacity-100'
                    : 'w-0 opacity-0 pointer-events-none',
                )}
                aria-hidden={!stableSecondaryNavigation.length}
              >
                <div className="flex w-full flex-col items-center py-5">
                  <div className="w-full px-2 flex flex-col gap-y-1">
                    {stableSecondaryNavigation.length
                      ? stableSecondaryNavigation.map(
                          (item: any, idx: number) => {
                            // Desktop: "back_to_overview" im Property-Edit-Submenu
                            // muss executeAppBack nutzen, nicht statische Route.
                            if (item.name === 'back_to_overview') {
                              return (
                                <BackButtonSidebarItem
                                  key={idx}
                                  fallbackTo={
                                    AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_EVALUATION
                                  }
                                  fallbackParams={{
                                    lang: params.lang,
                                    companyId: params.companyId,
                                    contactId: params.contactId,
                                    dossierId: params.dossierId,
                                  }}
                                  fallbackHash={getPropertyEditFallbackHashValue()}
                                  className={clsx(
                                    item.active
                                      ? 'bg-primary-800 text-white shadow-sm'
                                      : 'text-gray-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-800/30 hover:text-primary-700 dark:hover:text-white',
                                  )}
                                />
                              );
                            }
                            return (
                              <MenuItemLink
                                key={idx}
                                item={item}
                                className={clsx(
                                  item.hidden && !item.active ? 'hidden' : '',
                                  item.active
                                    ? 'bg-primary-800 text-white shadow-sm'
                                    : 'text-gray-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-800/30 hover:text-primary-700 dark:hover:text-white',
                                  'relative group w-full px-2 py-3 rounded-lg flex flex-col items-center gap-y-2 text-[11px] font-medium transition-colors',
                                )}
                                aria-current={item.active ? 'page' : undefined}
                              >
                                <Icon
                                  icon={item.icon}
                                  className={clsx(
                                    item.active
                                      ? 'text-white'
                                      : 'text-gray-500 dark:text-gray-400 group-hover:text-primary-700 dark:group-hover:text-white',
                                    'h-6 w-6 transition-colors',
                                  )}
                                  aria-hidden="true"
                                />
                                <span className="text-center leading-tight">
                                  {renderNavLabel(item, { theme: 'dark' })}
                                </span>
                                {item.showNotification && (
                                  <span className="absolute top-1.5 right-1.5 block h-2 w-2 rounded-full bg-orange-500 ring-2 ring-white dark:ring-[#18181b]" />
                                )}
                              </MenuItemLink>
                            );
                          },
                        )
                      : null}
                  </div>
                </div>
              </div>
              <div className="app-canvas min-w-0 w-full bg-white dark:bg-[#0a0a0b] flex-1 flex flex-col">
                {matchRoute({
                  to: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_INDEX,
                  fuzzy: true,
                } as any) ? (
                  <a
                    href={AppRoute.ROOT}
                    className="block bg-yellow-300 border-yellow-600 border-b-2 text-sm"
                  >
                    <span className="block max-w-7xl mx-auto px-4 md:px-10 py-4">
                      <span>{t('demo_banner_text')}</span>
                    </span>
                  </a>
                ) : null}
                <header className="bg-white dark:bg-[#18181b] border-b border-gray-200 dark:border-[#27272a] z-20 w-full relative shadow-sm shrink-0">
                  <div className="max-w-7xl mx-auto w-full min-w-0 px-4 md:px-10 py-3 flex justify-between items-center gap-2 sm:gap-4">
                    {query.data ? (
                      <>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {search.valiantDeviceId ? (
                            <Link
                              className="flex h-11 w-11 -m-1 shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-primary-50 text-gray-500 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors"
                              to={
                                AppRoute.$LANG_$COMPANYID_PUBLIC_VALIANT_$VALIANTDEVICEID
                              }
                              params={{
                                ...params,
                                valiantDeviceId: search.valiantDeviceId,
                              }}
                            >
                              <ChevronLeftIcon className="w-5 h-5" />
                            </Link>
                          ) : (
                            !matchRoute({ to: AppRoute.ROOT } as any) &&
                            !matchRoute({ to: '/' } as any) &&
                            // Mobile only: on desktop the submenu already
                            // provides back navigation where it makes sense,
                            // so the header back button would be redundant.
                            (isPropertyEditPage ? (
                              <BackButton
                                className="md:hidden"
                                fallbackTo={
                                  AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_EVALUATION
                                }
                                fallbackParams={{
                                  lang: params.lang,
                                  companyId: params.companyId,
                                  contactId: params.contactId,
                                  dossierId: params.dossierId,
                                }}
                                fallbackHash={getPropertyEditFallbackHashValue()}
                              />
                            ) : (
                              <BackButton className="md:hidden" />
                            ))
                          )}

                          <MenuItemLink
                            item={rootMenuItem}
                            className="group flex min-w-0 items-center gap-2.5"
                          >
                            {/* Mobile Logo */}
                            <div className="md:hidden flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gray-50 ring-1 ring-gray-200 dark:bg-white/10 dark:ring-white/20 transition-colors group-hover:bg-primary-50 group-hover:ring-primary-100 dark:group-hover:bg-white/15 dark:group-hover:ring-white/40">
                              <Logo className="h-6 w-auto" />
                            </div>

                            {(() => {
                              if (
                                matchRoute({ to: AppRoute.ROOT } as any) ||
                                matchRoute({ to: '/' } as any)
                              ) {
                                // Landing/Root screen -> show generic brand header
                                return (
                                  <span className="flex min-w-0 flex-col justify-center leading-tight">
                                    <span className="truncate text-base md:text-lg font-semibold tracking-tight text-gray-900 dark:text-[#f4f4f5] transition-colors group-hover:text-primary-600">
                                      ImmoDossier
                                    </span>
                                    <span className="truncate text-[11px] font-medium text-gray-400">
                                      by AgentSelly
                                    </span>
                                  </span>
                                );
                              }

                              const isObjectRoute = Boolean(
                                routerState.location?.pathname?.match(
                                  /\/(cockpit|dossier)\/([^/]+)/,
                                ),
                              );

                              if (pageTitleData.isLoading) {
                                return (
                                  <span className="flex min-w-0 flex-col justify-center leading-tight">
                                    <span className="h-5 w-44 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                                    <span className="mt-1 h-3 w-16 animate-pulse rounded bg-gray-100 dark:bg-gray-800 md:hidden" />
                                  </span>
                                );
                              }

                              const address =
                                pageTitleData.title ||
                                (!isObjectRoute
                                  ? query.data?.dossierTitle
                                  : undefined);
                              if (address) {
                                return (
                                  <span className="flex min-w-0 flex-col justify-center leading-tight">
                                    <span className="truncate text-base md:text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100 transition-colors group-hover:text-primary-600">
                                      {address}
                                    </span>
                                    <span className="truncate text-[10px] font-medium text-gray-400 md:hidden">
                                      by AgentSelly
                                    </span>
                                  </span>
                                );
                              }
                              return (
                                <span className="flex min-w-0 flex-col justify-center leading-tight">
                                  <span className="truncate text-base md:text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100 transition-colors group-hover:text-primary-600">
                                    {pageTitleData.title ||
                                      (!isObjectRoute
                                        ? query.data?.dossierTitle
                                        : undefined) ||
                                      'ImmoDossier'}
                                  </span>
                                  <span className="truncate text-[10px] font-medium text-gray-400">
                                    by AgentSelly
                                  </span>
                                </span>
                              );
                            })()}
                          </MenuItemLink>

                          {!pageTitleData.isLoading && pageTitleData.badge && (
                            <div className="hidden lg:flex items-center gap-1.5 text-xs text-gray-600 flex-shrink-0">
                              {pageTitleData.badge.rooms && (
                                <span className="px-2 py-0.5 bg-gray-100 rounded whitespace-nowrap">
                                  {pageTitleData.badge.rooms} Zi
                                </span>
                              )}
                              {pageTitleData.badge.category && (
                                <span className="px-2 py-0.5 bg-gray-100 rounded whitespace-nowrap">
                                  {pageTitleData.badge.category}
                                </span>
                              )}
                              {pageTitleData.badge.price && (
                                <span className="px-2 py-0.5 bg-gray-100 rounded font-medium whitespace-nowrap">
                                  {new Intl.NumberFormat('de-CH', {
                                    style: 'currency',
                                    currency: 'CHF',
                                    maximumFractionDigits: 0,
                                  }).format(pageTitleData.badge.price)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {userNavigation.length ? (
                          <div
                            className="relative"
                            ref={userMenuRef}
                          >
                            {isSignedIn ? (
                              <button
                                onClick={() =>
                                  setIsUserMenuOpen(!isUserMenuOpen)
                                }
                                className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-300"
                                aria-haspopup="menu"
                                aria-expanded={isUserMenuOpen}
                              >
                                {user?.imageUrl ? (
                                  <img
                                    src={user.imageUrl}
                                    alt={user.fullName || ''}
                                    className="h-8 w-8 border-2 border-gray-300 rounded-full bg-gray-100 object-cover object-center"
                                    onError={(e) => {
                                      (
                                        e.target as HTMLImageElement
                                      ).style.display = 'none';
                                      (
                                        e.target as HTMLImageElement
                                      ).nextElementSibling?.classList.remove(
                                        'hidden',
                                      );
                                    }}
                                  />
                                ) : null}
                                <div
                                  className={`h-8 w-8 border-2 border-gray-300 rounded-full bg-gray-100 flex items-center justify-center ${
                                    user?.imageUrl ? 'hidden' : ''
                                  }`}
                                >
                                  <svg
                                    className="h-5 w-5 text-gray-400"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                  </svg>
                                </div>
                              </button>
                            ) : null}
                            {isUserMenuOpen && (
                              <div
                                className="app-user-menu absolute right-0 top-full mt-2 w-72 bg-white border border-gray-200 rounded-md shadow-lg p-3 z-30"
                                role="menu"
                                aria-label="User menu"
                              >
                                {/* User header info (desktop) */}
                                <div className="hidden md:flex items-center gap-3 p-2 rounded-md">
                                  {user?.imageUrl ? (
                                    <img
                                      src={user.imageUrl}
                                      alt={user?.fullName || ''}
                                      className="h-10 w-10 rounded-full border border-gray-200 object-cover"
                                      onError={(e) => {
                                        (
                                          e.target as HTMLImageElement
                                        ).style.display = 'none';
                                        (
                                          e.target as HTMLImageElement
                                        ).nextElementSibling?.classList.remove(
                                          'hidden',
                                        );
                                      }}
                                    />
                                  ) : null}
                                  <div
                                    className={`h-10 w-10 rounded-full border border-gray-200 bg-gray-100 flex items-center justify-center ${
                                      user?.imageUrl ? 'hidden' : ''
                                    }`}
                                  >
                                    <svg
                                      className="h-6 w-6 text-gray-400"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                    </svg>
                                  </div>
                                  <div className="flex flex-col min-w-0">
                                    {user?.fullName ? (
                                      <>
                                        <span className="text-sm font-semibold text-gray-900 truncate">
                                          {user.fullName}
                                        </span>
                                        {user?.primaryEmailAddress
                                          ?.emailAddress && (
                                          <span className="text-xs text-gray-500 truncate">
                                            {
                                              user.primaryEmailAddress
                                                .emailAddress
                                            }
                                          </span>
                                        )}
                                      </>
                                    ) : user?.primaryEmailAddress
                                        ?.emailAddress ? (
                                      <span className="text-sm font-semibold text-gray-900 truncate">
                                        {user.primaryEmailAddress.emailAddress}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                                {(user?.fullName ||
                                  user?.primaryEmailAddress?.emailAddress) && (
                                  <hr className="my-2 border-gray-200 hidden md:block" />
                                )}

                                <ul
                                  className="flex flex-col gap-1"
                                  role="none"
                                >
                                  {userNavigation.map(
                                    (item: any, idx: number) => (
                                      <li
                                        key={idx}
                                        role="presentation"
                                      >
                                        <MenuItemLink
                                          item={item}
                                          role="menuitem"
                                          className="block w-full text-left px-3 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-300"
                                        />
                                      </li>
                                    ),
                                  )}
                                  <li role="presentation">
                                    <div className="block w-full text-left px-3 py-2 rounded-md text-sm text-red-600 hover:bg-gray-50 active:bg-gray-100 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-300">
                                      <SignOutButton
                                        redirectUrl={signOutRedirectUrl}
                                      >
                                        {t('user_logout')}
                                      </SignOutButton>
                                    </div>
                                  </li>
                                </ul>
                              </div>
                            )}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <p className="text-xl">&nbsp;</p>
                        <div className="dropdown dropdown-end">
                          <div className="rounded-full">&nbsp;</div>
                        </div>
                      </>
                    )}
                  </div>
                </header>

                <main
                  data-scroll-restoration-id="main-scroll-container"
                  className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain pb-mobilenav md:pb-0"
                >
                  <div className="max-w-7xl mx-auto w-full px-4 md:px-10 py-4 relative">
                    {clerkLoadTimedOut ? (
                      <AuthLoadErrorWidget />
                    ) : query.isError ? (
                      <ErrorWidget
                        error={query.error}
                        onRetry={() => window.location.reload()}
                      />
                    ) : (
                      props.children
                    )}
                  </div>

                  <footer className="max-w-7xl mx-auto footer px-4 md:px-10 py-4 hidden md:flex">
                    <aside className="items-center grid-flow-col w-full">
                      <div className="flex gap-6">
                        <p>© {new Date().getFullYear()} AgentSelly</p>
                        <div>
                          <a
                            href="https://www.agentselly.ch/agb/"
                            target="_blank"
                            rel="noreferrer"
                            className="link link-hover"
                          >
                            AGB
                          </a>
                          {' | '}
                          <a
                            href="https://www.agentselly.ch/impressum/"
                            target="_blank"
                            rel="noreferrer"
                            className="link link-hover"
                          >
                            Impressum
                          </a>
                        </div>
                      </div>
                    </aside>
                    <nav className="md:place-self-center md:justify-self-end">
                      <div className="grid grid-flow-col gap-4">
                        {/* Facebook */}
                        <a
                          href="https://www.facebook.com/agentselly"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            height="24"
                            width="24"
                            id="Facebook-Logo--Streamline-Ultimate"
                          >
                            <desc>
                              Facebook Logo Streamline Icon:
                              https://streamlinehq.com
                            </desc>
                            <path
                              d="M18.0113 7.59H13.8287V5.728C13.7495 5.1376 14.2255 4.6202 14.8204 4.65C15.2301 4.65 17.7487 4.6598 17.7487 4.6598V0.73H13.5063C9.6578 0.73 8.9287 3.6445 8.9287 5.4879V7.59H5.9887V11.51H8.9287V23.27H13.8287V11.51H17.6027Z"
                              fill="none"
                              className="stroke-gray-900"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1"
                            ></path>
                          </svg>
                        </a>
                        {/* Twitter */}
                        <a
                          href="https://twitter.com/agentselly"
                          target="_blank"
                          rel="noreferrer"
                          className="link link-hover"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            height="24"
                            width="24"
                            id="Twitter-Logo--Streamline-Ultimate"
                          >
                            <desc>
                              Twitter Logo Streamline Icon:
                              https://streamlinehq.com
                            </desc>
                            <path
                              d="M23.2487 5.932L21.2035 5.4207L22.2261 3.3755L19.7064 4.0913C17.1276 1.6855 12.9116 2.9735 12.1176 6.4096C12.0375 6.756 11.9981 7.1104 12 7.4659V8.4885C8.381 9.235 5.216 7.2614 2.2852 3.8868Q1.5183 7.9772 3.8191 10.0224L0.7513 9.5111C1.1655 11.6269 2.1441 13.2948 4.8417 13.6015L2.2852 14.6242C3.3078 16.6694 4.9092 16.9864 7.3982 17.1807C5.4624 18.5639 3.1299 19.2816 0.7513 19.2259C13.7947 25.0231 21.2035 16.5017 21.2035 8.9998V7.6418Z"
                              fill="none"
                              className="stroke-gray-900"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1"
                            ></path>
                          </svg>
                        </a>
                        {/* LinkedIn */}
                        <a
                          href="https://www.linkedin.com/company/agentselly"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            height="24"
                            width="24"
                            id="Linkedin-Logo--Streamline-Ultimate"
                          >
                            <desc>
                              Linkedin Logo Streamline Icon:
                              https://streamlinehq.com
                            </desc>
                            <defs></defs>
                            <title>professional-network-linkedin</title>
                            <path
                              d="M6.12 22.6909H0.7745V8.7927H6.12ZM15.7418 13.0691C14.5609 13.069 13.6036 14.0264 13.6036 15.2073V22.6909H8.2582V8.7927H13.6036V10.3803C14.7971 9.3689 16.3051 8.8038 17.8693 8.782C21.036 8.782 23.2255 11.134 23.2255 15.5761V22.6909H17.88V15.2073C17.88 14.0264 16.9227 13.0691 15.7418 13.0691ZM6.12 3.9818C6.12 6.0393 3.8927 7.3252 2.1109 6.2965C0.3291 5.2678 0.3291 2.6959 2.1109 1.6672C2.5172 1.4326 2.9781 1.3091 3.4473 1.3091C4.9234 1.309 6.12 2.5057 6.12 3.9818Z"
                              fill="none"
                              className="stroke-gray-900"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1"
                            ></path>
                          </svg>
                        </a>
                        {/* Instagram */}
                        <a
                          href="https://www.instagram.com/agentselly_ag/"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            height="24"
                            width="24"
                            id="Instagram-Logo--Streamline-Ultimate"
                          >
                            <desc>
                              Instagram Logo Streamline Icon:
                              https://streamlinehq.com
                            </desc>
                            <path
                              className="stroke-gray-900"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M18.5 0.5h-13c-2.76142 0 -5 2.23858 -5 5v13c0 2.7614 2.23858 5 5 5h13c2.7614 0 5 -2.2386 5 -5v-13c0 -2.76142 -2.2386 -5 -5 -5Z"
                              strokeWidth="1"
                            ></path>
                            <path
                              className="stroke-gray-900"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 16c2.2091 0 4 -1.7909 4 -4 0 -2.20914 -1.7909 -4 -4 -4 -2.20914 0 -4 1.79086 -4 4 0 2.2091 1.79086 4 4 4Z"
                              strokeWidth="1"
                            ></path>
                            <path
                              className="stroke-gray-900"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M16 4H8C5.79086 4 4 5.79086 4 8v8c0 2.2091 1.79086 4 4 4h8c2.2091 0 4 -1.7909 4 -4V8c0 -2.20914 -1.7909 -4 -4 -4Z"
                              strokeWidth="1"
                            ></path>
                            <g>
                              <path
                                className="stroke-gray-900"
                                d="M17 8c-0.2761 0 -0.5 -0.22386 -0.5 -0.5s0.2239 -0.5 0.5 -0.5"
                                strokeWidth="1"
                              ></path>
                              <path
                                className="stroke-gray-900"
                                d="M17 8c0.2761 0 0.5 -0.22386 0.5 -0.5S17.2761 7 17 7"
                                strokeWidth="1"
                              ></path>
                            </g>
                          </svg>
                        </a>
                      </div>
                    </nav>
                  </footer>
                </main>

                {/* Bottom Tab Menu visible only on mobile */}
                <nav className="app-mobilenav md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-gray-100 bg-white/95 backdrop-blur-md shadow-[0_-1px_8px_rgba(0,0,0,0.04)] dark:border-[#27272a] dark:bg-[#0a0a0b]/95 pb-safe">
                  <div className="flex w-full items-stretch px-1">
                    {primaryNavigation
                      .filter((item: MenuItem) => item.mobile)
                      .map((item: MenuItem, idx: number) => {
                        const isDemo =
                          !!item.name && item.name.includes('demo');
                        return (
                          <MenuItemLink
                            key={idx}
                            item={item}
                            className="group flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-[56px]"
                          >
                            <span
                              className={clsx(
                                'flex h-8 w-12 items-center justify-center rounded-full transition-colors',
                                item.active
                                  ? 'bg-primary-50 dark:bg-primary-500/15'
                                  : 'bg-transparent group-hover:bg-gray-100 dark:group-hover:bg-white/5',
                              )}
                            >
                              <Icon
                                icon={item.icon}
                                className={clsx('h-5 w-5 transition-colors', {
                                  'text-primary-600 dark:text-primary-400':
                                    item.active,
                                  'text-gray-500 dark:text-gray-400':
                                    !item.active && !isDemo,
                                  'text-gray-400 dark:text-gray-600':
                                    !item.active && isDemo,
                                })}
                              />
                            </span>
                            <span
                              className={clsx(
                                'w-full truncate text-center text-[11px] leading-none transition-colors',
                                {
                                  'font-medium text-primary-600 dark:text-primary-400':
                                    item.active,
                                  'text-gray-500 dark:text-gray-400':
                                    !item.active && !isDemo,
                                  'text-gray-400 dark:text-gray-600':
                                    !item.active && isDemo,
                                },
                              )}
                            >
                              {renderNavLabel(item, {
                                theme: 'light',
                                stack: true,
                              })}
                            </span>
                          </MenuItemLink>
                        );
                      })}
                    <button
                      type="button"
                      onClick={openMobileMenu}
                      className="group flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-[56px]"
                    >
                      <span className="flex h-8 w-12 items-center justify-center rounded-full transition-colors group-hover:bg-gray-100 dark:group-hover:bg-white/5">
                        <MenuIcon className="h-5 w-5 text-gray-500 transition-colors group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-200" />
                      </span>
                      <span className="text-[11px] leading-none text-gray-500 dark:text-gray-400">
                        {t('nav_menu', 'Menü')}
                      </span>
                    </button>
                  </div>
                </nav>

                {/* Mobile menu overlay + drawer (always mounted drawer for slide-in) */}
                {isMobileMenuOpen && (
                  <div
                    className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity duration-200 ease-out"
                    onClick={closeMobileMenu}
                  />
                )}
                <div
                  className={clsx(
                    'fixed inset-y-0 right-0 z-50 w-[85vw] max-w-[340px] bg-white shadow-xl dark:shadow-gray-900/80 border-l border-gray-200 dark:border-gray-700 transform transition-transform duration-200 ease-out rounded-l-xl',
                    query.data?.v3Redesign
                      ? 'dark:bg-zinc-900'
                      : 'dark:bg-gray-800',
                    isMobileMenuOpen
                      ? 'translate-x-0'
                      : 'translate-x-full pointer-events-none',
                  )}
                  role="dialog"
                  aria-modal={isMobileMenuOpen ? 'true' : undefined}
                >
                  <div className="flex flex-col h-full p-4">
                    <div className="flex items-center justify-between mb-4">
                      <a
                        className="flex items-center space-x-3 text-gray-900 dark:text-gray-100 text-lg font-semibold"
                        href="/"
                      >
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gray-50 ring-1 ring-gray-200 dark:bg-white/10 dark:ring-white/20 transition-colors group-hover:bg-primary-50 group-hover:ring-primary-100">
                          <Logo className="h-6 w-auto" />
                        </div>
                        <span className="flex min-w-0 flex-col justify-center leading-tight">
                          <span className="truncate text-base font-semibold tracking-tight text-gray-900 dark:text-gray-100 transition-colors">
                            ImmoDossier
                          </span>
                        </span>
                      </a>
                      <button
                        onClick={closeMobileMenu}
                        className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 dark:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-300 dark:focus-visible:ring-gray-700"
                        aria-label="Close menu"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="mt-3 flex-1 overflow-y-auto">
                      <nav className="flex h-full flex-col">
                        <div className="space-y-2">
                          {primaryNavigation.map(
                            (item: MenuItem, idx: number) => {
                              // Demo entry: render a non-navigating section header
                              // with its Dossier/Cockpit choices indented beneath,
                              // so the user can pick a preview right here.
                              if (
                                item.name === 'nav_demo' &&
                                demoChildren.length > 0
                              ) {
                                return (
                                  <div
                                    key={idx}
                                    className="space-y-1"
                                  >
                                    <div className="flex items-center space-x-3 px-3 pt-1 pb-0.5">
                                      <Icon
                                        icon={item.icon}
                                        className="h-6 w-6 flex-shrink-0 text-gray-400"
                                      />
                                      <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                                        {renderNavLabel(item, {
                                          theme: 'light',
                                        })}
                                      </span>
                                    </div>
                                    <div className="space-y-1 pl-4">
                                      {demoChildren.map(
                                        (child: MenuItem, cidx: number) => (
                                          <MenuItemLink
                                            key={cidx}
                                            item={child}
                                            onClick={closeMobileMenu}
                                            className={clsx(
                                              'flex items-center space-x-3 p-3 rounded-md border text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 active:bg-gray-100 dark:active:bg-gray-800 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-300 dark:focus-visible:ring-gray-700',
                                              child.active &&
                                                'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border-primary-200 dark:border-primary-800',
                                            )}
                                          >
                                            <Icon
                                              icon={child.icon}
                                              className={clsx(
                                                'h-6 w-6 flex-shrink-0',
                                                {
                                                  'text-primary-600':
                                                    child.active,
                                                  'text-gray-500 dark:text-gray-400':
                                                    !child.active,
                                                },
                                              )}
                                            />
                                            <span className="text-base font-medium">
                                              {child.name === 'nav_demo_dossier'
                                                ? t('nav_dossier', 'Dossier')
                                                : child.name ===
                                                  'nav_demo_cockpit'
                                                ? t('nav_cockpit', 'Cockpit')
                                                : t(child.name)}
                                            </span>
                                          </MenuItemLink>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                );
                              }
                              return (
                                <MenuItemLink
                                  key={idx}
                                  item={item}
                                  onClick={closeMobileMenu}
                                  className={clsx(
                                    'flex items-center space-x-3 p-3 rounded-md border text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 active:bg-gray-100 dark:active:bg-gray-800 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-300 dark:focus-visible:ring-gray-700',
                                    item.active &&
                                      'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border-primary-200 dark:border-primary-800',
                                  )}
                                >
                                  <Icon
                                    icon={item.icon}
                                    className={clsx('h-6 w-6 flex-shrink-0', {
                                      'text-primary-600 dark:text-primary-400':
                                        item.active,
                                      'text-gray-500 dark:text-gray-400':
                                        !item.active,
                                    })}
                                  />
                                  <span className="text-base font-medium">
                                    {renderNavLabel(item, { theme: 'light' })}
                                  </span>
                                </MenuItemLink>
                              );
                            },
                          )}
                          {primaryNavigation.length &&
                          stableSecondaryNavigation.length ? (
                            <hr className="border-gray-200 dark:border-gray-700 my-4" />
                          ) : null}
                          {stableSecondaryNavigation.map(
                            (item: MenuItem, idx: number) => {
                              // Mobile drawer: "back_to_overview" muss
                              // executeAppBack nutzen, nicht statische Route.
                              if (item.name === 'back_to_overview') {
                                return (
                                  <BackButtonSidebarItem
                                    key={idx}
                                    fallbackTo={
                                      AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_EVALUATION
                                    }
                                    fallbackParams={{
                                      lang: params.lang,
                                      companyId: params.companyId,
                                      contactId: params.contactId,
                                      dossierId: params.dossierId,
                                    }}
                                    fallbackHash={getPropertyEditFallbackHashValue()}
                                    className={clsx(
                                      'flex items-center space-x-3 p-3 rounded-md border text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 active:bg-gray-100 dark:active:bg-gray-800 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-300 dark:focus-visible:ring-gray-700',
                                      item.active &&
                                        'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border-primary-200 dark:border-primary-800',
                                    )}
                                  />
                                );
                              }
                              return (
                                <MenuItemLink
                                  key={idx}
                                  item={item}
                                  onClick={closeMobileMenu}
                                  className={clsx(
                                    'flex items-center space-x-3 p-3 rounded-md border text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 active:bg-gray-100 dark:active:bg-gray-800 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-300 dark:focus-visible:ring-gray-700',
                                    item.active &&
                                      'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border-primary-200 dark:border-primary-800',
                                  )}
                                >
                                  <Icon
                                    icon={item.icon}
                                    className={clsx('h-6 w-6 flex-shrink-0', {
                                      'text-primary-600 dark:text-primary-400':
                                        item.active,
                                      'text-gray-500 dark:text-gray-400':
                                        !item.active,
                                    })}
                                  />
                                  <span className="text-base font-medium">
                                    {renderNavLabel(item, { theme: 'light' })}
                                  </span>
                                </MenuItemLink>
                              );
                            },
                          )}
                        </div>
                      </nav>
                    </div>
                    <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        © {new Date().getFullYear()} AgentSelly
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Render the SecureAssets iframe only outside of demo routes */}
              {query.data?.secureAssetIframeUrl &&
              /\/storeCookie\/[^/]+/.test(query.data.secureAssetIframeUrl) &&
              !matchRoute({
                to: AppRoute.$LANG_$COMPANYID_PUBLIC_DEMO_INDEX,
                fuzzy: true,
              } as any) ? (
                <iframe
                  title="2wgarnDCHx"
                  style={{
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '1px',
                    height: '1px',
                  }}
                  src={query.data.secureAssetIframeUrl}
                />
              ) : null}
            </div>
          </GlobalLoaderProvider>
        </AuthzProvider>
      </AssetConfigProvider>
    </ThemeProvider>
  );
}

interface LayoutProps {
  readonly children: React.ReactNode;
}
